import { assertWriteIdentity, captureWriteIdentity, type WriteIdentity } from '@/core/api/http'
import type { FormValues } from '@/core/components/form/formModel'
import type { FieldId, Failure, Result } from '@/core/lib/result'
import { technical } from '@/core/lib/result'
import type { FieldError, FieldErrors } from '@/core/lib/fieldError'
import type { PresenceRules } from '@/core/lib/validate'
import { validatePresence } from '@/core/lib/validate'
import { isJobcenter } from '../lib/berichtTemplate'
import { renderBericht } from '../lib/renderBericht'
import { renderAnwesenheitsliste } from '../lib/renderAnwesenheitsliste'
import { getDocData, getDocBucketAddress, patchDocData } from '@/core/data/dokumente'
import { getCoachingVertragRef, setAufgabe, type AdvanceResult } from '@/core/data/coachings'

const BERICHT_DOC = 'abschlussbericht'

const BEURTEILUNG_KENNTNISSE: FieldId = 'Beurteilung.Kenntnisse und Fertigkeiten'
const BEURTEILUNG_EIGENSCHAFTEN: FieldId = 'Beurteilung.Persönliche Eigenschaften und Stärken'
const INTEGRATIONSCHANCEN: FieldId = 'Integrationschancen.Bewertung der Integrationschancen'

export async function loadBerichtForm(coachingId: string): Promise<Record<string, unknown> | null> {
  const doc = await getDocData(coachingId, BERICHT_DOC)
  return (doc?.form as Record<string, unknown> | undefined) ?? null
}

export async function saveBerichtDraft(coachingId: string, values: FormValues, identity: WriteIdentity = captureWriteIdentity()): Promise<void> {
  await patchDocData(coachingId, BERICHT_DOC, { form: values }, undefined, identity)
}

export async function getBerichtPath(coachingId: string): Promise<string | null> {
  return getDocBucketAddress(coachingId, BERICHT_DOC)
}

export async function getBerichtBehoerde(coachingId: string): Promise<string | null> {
  const ref = await getCoachingVertragRef(coachingId)
  return ref?.agentur?.behoerde ?? null
}

export function berichtPresenceRules(behoerde: string | null | undefined): PresenceRules {
  const required: FieldId[] = [BEURTEILUNG_KENNTNISSE, BEURTEILUNG_EIGENSCHAFTEN]
  if (!isJobcenter(behoerde)) required.push(INTEGRATIONSCHANCEN)
  return { required }
}

export function berichtManualFields(behoerde: string | null | undefined): ReadonlySet<FieldId> {
  return new Set(berichtPresenceRules(behoerde).required)
}

export type BerichtCheck = { errors: FieldErrors }

export function checkBericht(
  values: Record<string, unknown>,
  behoerde: string | null | undefined,
): BerichtCheck {
  const { missing } = validatePresence(values, berichtPresenceRules(behoerde))
  const errors = new Map<FieldId, FieldError>()
  for (const path of missing) errors.set(path, { kind: 'missing' })
  return { errors }
}

export async function generateBericht(
  coachingId: string,
  values: FormValues,
  identity: WriteIdentity = captureWriteIdentity(),
): Promise<Result<void>> {
  try {
    await saveBerichtDraft(coachingId, values, identity)
    assertWriteIdentity(identity)
    const rendered = await renderBericht(coachingId, identity)
    assertWriteIdentity(identity)
    if (!rendered.ok) return rendered
    await setAufgabe(coachingId, 'abschlussberichtPruefen', identity)
    assertWriteIdentity(identity)
    return rendered
  } catch (e) {
    assertWriteIdentity(identity)
    console.error("Abschlussbericht konnte nicht erstellt werden:", e)
    return technical(
      "Abschlussbericht konnte nicht erstellt werden. " + ((e as Error)?.message ?? String(e)),
    )
  }
}

export async function confirmBericht(coachingId: string, identity: WriteIdentity = captureWriteIdentity()): Promise<AdvanceResult | Failure> {
  const rendered = await renderAnwesenheitsliste(coachingId, identity)
  assertWriteIdentity(identity)
  if (!rendered.ok) return rendered
  return setAufgabe(coachingId, 'teilnehmendenlistePruefen', identity)
}
